import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Sparkles, Copy, Save, Loader2, Lightbulb } from 'lucide-react';

// Shared OpenAI-powered SEO + GEO suggestion panel — used on the member
// micro-site settings and the course editor.
export default function SeoAssistant({ title, description, suggestUrl, saveUrl, authHeader, initial, onSaved }) {
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seo, setSeo] = useState(initial || null);
  const [tips, setTips] = useState([]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(suggestUrl, {}, { headers: authHeader });
      setSeo({
        seo_title: res.data.seo_title || '',
        meta_description: res.data.meta_description || '',
        keywords: res.data.keywords || [],
        geo_summary: res.data.geo_summary || '',
      });
      setTips(res.data.tips || []);
      toast.success('AI suggestions ready — tweak anything, then save');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI suggestion failed — please try again');
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!seo) return;
    setSaving(true);
    try {
      await axios.put(saveUrl, seo, { headers: authHeader });
      toast.success('SEO settings saved');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const copyAll = async () => {
    if (!seo) return;
    const text = `Title: ${seo.seo_title}\nDescription: ${seo.meta_description}\nKeywords: ${(seo.keywords || []).join(', ')}\nAI-search summary: ${seo.geo_summary}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="rounded-2xl bg-brand-surface border border-white/5 p-6 space-y-4" data-testid="seo-assistant">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-brand-gold" /> {title}
          </h3>
          <p className="text-xs text-gray-400 mt-1 max-w-xl">{description}</p>
        </div>
        <Button
          onClick={generate}
          disabled={generating}
          className="bg-brand-gold text-black hover:brightness-110 text-xs"
          data-testid="seo-generate-btn"
        >
          {generating ? (
            <>
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Thinking…
            </>
          ) : (
            <>
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {seo ? 'Regenerate' : 'Generate suggestions'}
            </>
          )}
        </Button>
      </div>

      {seo && (
        <div className="space-y-3" data-testid="seo-results">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500">SEO title ({(seo.seo_title || '').length}/60)</label>
            <Input
              value={seo.seo_title}
              onChange={(e) => setSeo({ ...seo, seo_title: e.target.value })}
              className="mt-1 bg-white/5 border-white/10 text-white text-sm"
              data-testid="seo-title-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500">Meta description ({(seo.meta_description || '').length}/155)</label>
            <Textarea
              value={seo.meta_description}
              onChange={(e) => setSeo({ ...seo, meta_description: e.target.value })}
              rows={2}
              className="mt-1 bg-white/5 border-white/10 text-white text-sm"
              data-testid="seo-description-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500">Keywords</label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="seo-keywords">
              {(seo.keywords || []).map((k) => (
                <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-brand-gold/10 text-brand-gold border border-brand-gold/25">
                  {k}
                </span>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500">GEO — AI-search summary (quoted by ChatGPT/Perplexity)</label>
            <Textarea
              value={seo.geo_summary}
              onChange={(e) => setSeo({ ...seo, geo_summary: e.target.value })}
              rows={3}
              className="mt-1 bg-white/5 border-white/10 text-white text-sm"
              data-testid="seo-geo-input"
            />
          </div>

          {tips.length > 0 && (
            <div className="rounded-xl bg-white/[0.03] border border-white/5 p-3">
              <p className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-1.5">
                <Lightbulb className="w-3 h-3 text-brand-gold" /> Visibility tips
              </p>
              <ul className="space-y-1 text-xs text-gray-300 list-disc pl-4">
                {tips.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              data-testid="seo-save-btn"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving…' : 'Apply & Save'}
            </Button>
            <Button
              variant="outline"
              onClick={copyAll}
              className="border-white/10 text-white hover:bg-white/5 text-xs"
              data-testid="seo-copy-btn"
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy all
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
